import { ChangeDetectionStrategy, Component, Signal, computed, effect, inject, signal, untracked } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { ESegmentedToggleComponent, ESegmentedToggleOption } from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { HdChartDataCache } from '../../library/hd-chart-data-cache.service';
import { HdBodygraphCenter, HdBodygraphChartPreset } from '../hd-bodygraph.models';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HD_NOT_SELF_BY_IMPACT_ORDER } from '../hd-center-not-self-metadata';
import { getHdPlanetDisplayConfig } from '../planet-grid/hd-planet-display-config';
import { HD_MIND_MOMENT_CATEGORY } from './hd-moment-fields';
import { HdMomentLinkDialogComponent, HdMomentLinkTarget } from './hd-moment-link-dialog.component';
import {
  HdMindConditionerKind,
  HdMindLogEntry,
  HdMindLogGroup,
  buildHdMindLogEntries,
  countHdMindLogByCenter,
  filterHdMindLogEntries,
  groupHdMindLogEntries,
  topHdMindConditioners,
} from './hd-mind-log.models';

/** Paneeldata: de chart waarvan het logboek getoond wordt, plus hoe een notitie geopend wordt. */
export interface HdMindLogPanelData {
  readonly chartId: string;
  readonly chartName: string;
  /** Preset van de chart (natal-kern voor "nieuwe mind-notitie"); null = geen nieuwe notities vanuit het paneel. */
  readonly preset: HdBodygraphChartPreset | null;
  /** Opent het moment in een nieuwe tab mét het Notities-paneel (de page voert dit uit). */
  readonly openChartWithNotes: (chartId: string) => void;
}

type HdMindLogKindFilter = HdMindConditionerKind | 'all';

interface HdMindConditionerBadge {
  readonly kind: 'transit' | 'person';
  readonly glyph: string | null;
  readonly color: string | null;
  readonly label: string;
}
/** `undefined` = alle centra, `null` = enkel notities zonder centrum. */
type HdMindLogCenterFilter = HdBodygraphCenter | null | undefined;

/**
 * Paneel "Mijn mind zegt" (spec 2026-10-22-mind-logboek §5): alle mind-notities
 * van een chart, gegroepeerd per open centrum in niet-zelf-impactvolgorde, met
 * de vaakst genoemde conditioneerders bovenaan en filters op centrum en soort.
 * Leest de relaties uit de gedeelde chart-datacache (zelfde bron als de
 * Momenten-sectie, geen extra API-call) en ververst na elke toevoeging.
 */
@Component({
  selector: 'hd-mind-log-panel',
  standalone: true,
  imports: [ESegmentedToggleComponent, HdMomentLinkDialogComponent],
  templateUrl: './hd-mind-log-panel.component.html',
  styleUrl: './hd-mind-log-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdMindLogPanelComponent {
  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as Signal<HdMindLogPanelData | null> | null;
  private readonly dataCache = inject(HdChartDataCache);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly data = computed<HdMindLogPanelData | null>(() => this.panelData?.() ?? null);
  protected readonly preset = computed(() => this.data()?.preset ?? null);

  protected readonly centerFilter = signal<HdMindLogCenterFilter>(undefined);
  protected readonly kindFilter = signal<HdMindLogKindFilter>('all');
  /** Target van de toevoeg-dialoog (nieuwe mind-notitie); null = gesloten. */
  protected readonly dialogTarget = signal<HdMomentLinkTarget | null>(null);
  protected readonly mindCategory = HD_MIND_MOMENT_CATEGORY;

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly text = computed(() => {
    const lang = this.lang();
    const translate = (key: string, params?: Record<string, unknown>) =>
      this.transloco.translate<string>(key, params, lang);
    return {
      newNote: translate('humanDesign.mindLog.newNote'),
      topConditioners: translate('humanDesign.mindLog.topConditioners'),
      filterCenter: translate('humanDesign.mindLog.filterCenter'),
      filterKind: translate('humanDesign.mindLog.filterKind'),
      allCenters: translate('humanDesign.mindLog.allCenters'),
      noCenter: translate('humanDesign.mindLog.noCenter'),
      empty: translate('humanDesign.mindLog.empty'),
      emptyFiltered: translate('humanDesign.mindLog.emptyFiltered'),
      open: translate('humanDesign.mindLog.open'),
      count: (count: number) => count === 1 ? translate('humanDesign.mindLog.countOne') : translate('humanDesign.mindLog.count', { count }),
      gate: (gate: number) => translate('humanDesign.mindLog.gate', { gate }),
      centerName: (center: HdBodygraphCenter) => translate(`humanDesign.properties.centerNames.${center}`),
      kindOptions: (['all', 'transit', 'person', 'none'] as const).map((value): ESegmentedToggleOption<HdMindLogKindFilter> => ({
        value,
        label: translate(`humanDesign.mindLog.kinds.${value}`),
      })),
    };
  });

  /** Alle mind-notities van de chart (chronologisch aflopend). */
  protected readonly entries = computed<HdMindLogEntry[]>(() => {
    const chartId = this.data()?.chartId ?? null;
    return buildHdMindLogEntries(this.dataCache.relations.value(chartId) ?? []);
  });

  protected readonly filtered = computed(() =>
    filterHdMindLogEntries(this.entries(), this.centerFilter(), this.kindFilter()));

  protected readonly groups = computed<HdMindLogGroup[]>(() => groupHdMindLogEntries(this.filtered()));

  protected readonly top = computed(() => topHdMindConditioners(this.entries()));

  /** Centrumchips: enkel centra met notities, in impactvolgorde, plus "zonder centrum" wanneer van toepassing. */
  protected readonly centerChips = computed(() => {
    const counts = countHdMindLogByCenter(this.entries());
    const chips: { center: HdBodygraphCenter | null; count: number }[] = HD_NOT_SELF_BY_IMPACT_ORDER
      .filter(center => (counts.get(center) ?? 0) > 0)
      .map(center => ({ center, count: counts.get(center) ?? 0 }));
    const without = counts.get(null) ?? 0;
    if (without > 0) chips.push({ center: null, count: without });
    return chips;
  });

  constructor() {
    // Relaties van de chart volgen via de gedeelde cache (ververst na elke wijziging).
    effect(onCleanup => {
      const chartId = this.data()?.chartId;
      if (chartId) onCleanup(this.dataCache.relations.watch(chartId));
    });
    // Andere chart in het paneel → filters terug naar de beginstand.
    effect(() => {
      this.data()?.chartId;
      untracked(() => {
        this.centerFilter.set(undefined);
        this.kindFilter.set('all');
      });
    });
  }

  protected setCenterFilter(center: HdMindLogCenterFilter): void {
    this.centerFilter.set(this.centerFilter() === center ? undefined : center);
  }

  protected setKindFilter(kind: HdMindLogKindFilter): void {
    this.kindFilter.set(kind);
  }

  protected openEntry(entry: HdMindLogEntry): void {
    this.data()?.openChartWithNotes(entry.chartId);
  }

  protected openNew(): void {
    const data = this.data();
    if (!data?.preset) return;
    this.dialogTarget.set({ chartId: data.chartId, chartName: data.chartName });
  }

  /** Na een toevoeging: de cache herladen; de nieuwe notitie verschijnt in zijn groep (niet apart openen). */
  protected onChanged(): void {
    const chartId = this.data()?.chartId;
    if (chartId) void this.dataCache.relations.refresh(chartId);
  }

  /** Eén badge per conditioneerder: transit als glyph + poort.lijn, persoon op naam met poorten. */
  protected conditionerBadges(entry: HdMindLogEntry): HdMindConditionerBadge[] {
    return entry.conditioners.map(conditioner => {
      if (conditioner.kind === 'transit') {
        const config = getHdPlanetDisplayConfig(conditioner.planetKey);
        return {
          kind: 'transit',
          glyph: config.astronomiconChar,
          color: config.stripeColor,
          label: conditioner.line != null ? `${conditioner.gate}.${conditioner.line}` : `${conditioner.gate}`,
        };
      }
      return {
        kind: 'person',
        glyph: null,
        color: null,
        label: conditioner.gates.length > 0 ? `${conditioner.chartName} · ${conditioner.gates.join(', ')}` : conditioner.chartName,
      };
    });
  }
}
